import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import SignageBrandCategoryForm from "components/AddEditBrandCategory";
import { uploadCloudFile, getCloudFileById, resolveImageId, hardDeleteCloudFile } from "components/Imageshelper";
type SelectOption = { value: string; label: string };
import { getAuthCookie } from "../../utils/authCookies";
const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const RealtorAddEditCategory: React.FC = () => {
  const { catId } = useParams<{ catId: string }>();
  const isEdit = !!catId;
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const token = getAuthCookie("sessionToken") || "";

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  const formik = useFormik<{ name: string; statuss: string; image: File | null }>({
    enableReinitialize: true,
    initialValues: { name: "", statuss: "Active", image: null },
    validationSchema: Yup.object({
      name: Yup.string().min(2).max(50).required("Name is required"),
      // statuss: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        let imageFileId = cloudFileId || ""; // keep existing if present
        
        // ✅ Delete old image if a new one is being uploaded
        if (values.image instanceof File && cloudFileId && /^[0-9a-f]{24}$/i.test(cloudFileId)) {
          try {
            await hardDeleteCloudFile(cloudFileId, token);
            console.log(`Old category image ${cloudFileId} deleted successfully`);
          } catch (deleteError) {
            console.warn(`Failed to delete old category image ${cloudFileId}:`, deleteError);
            // Continue even if delete fails - don't block the update
          }
        }
        
        // Upload new image
        if (values.image instanceof File) {
          const uploaded = await uploadCloudFile({ file: values.image, token });
          imageFileId = uploaded.id;
        }

        const generated_id = await resolveImageId(values.image, token, imageFileId);


        const payload = {
          name: values.name,
          image: generated_id,
          status: values.statuss === "Active",
        };

        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/categories/${catId}`
          : `${process.env.REACT_APP_API_URL}/categories`;
        const method = isEdit ? "PUT" : "POST";

        const res = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok || data?.error) throw new Error(data?.error || data?.message || "Save failed");

        showSuccess(isEdit ? "Category updated successfully!" : "Category added successfully!");
        navigate(`${baseRoute}/categories`);
      } catch (e: any) {
        showError(e?.message || (isEdit ? "Failed to update category." : "Failed to add category."));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isEdit) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/categories/${catId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || data?.error) throw new Error(data?.error || data?.message || "Fetch failed");
        if (!cancel) {
          formik.setValues({
            name: data?.name || "",
            statuss: data?.status ? "Active" : "Inactive",
            image: null,
          });
        }

        const img = data?.image || data?.image_file_id || data?.cloud_file_id || "";
        if (img) {
          // Legacy: stored a static /uploads path
          if (typeof img === "string" && (img.startsWith("uploads/") || img.includes("/uploads/"))) {
            setPreviewUrl(`${process.env.REACT_APP_BASE_URL}${img.replace(/^\//, "")}`);
            setCloudFileId(null);
          } else {
            // New: stored a cloud-drive file id
            try {
              const cf = await getCloudFileById(String(img), token);
              setPreviewUrl(cf.url || null);
              setCloudFileId(cf.id);
            } catch {
              // fallback: show nothing if file fetch fails
              setPreviewUrl(null);
              setCloudFileId(String(img));
            }
          }
        } else {
          setPreviewUrl(null);
          setCloudFileId(null);
        }
      } catch (e: any) {
        showError(e?.message || "Error fetching category");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [catId, isEdit, token]);


  if (loading) return <div className="content container">Loading…</div>;

  return (
    <SignageBrandCategoryForm
      title={isEdit ? "Edit Category" : "Add Category"}
      blockTitle="Category Details"
      infoTitle="Category Information"
      imageTitle="Category Image"
      nameLabel="Name"
      formik={formik}
      statusOptions={STATUS_OPTIONS}
      submitting={submitting}
      onBack={() => navigate(-1)}
      onSubmit={() => formik.handleSubmit()}
      previewUrl={previewUrl}
      onDropFiles={(files) => {
        const f = files[0];
        if (f) { formik.setFieldValue("image", f); setPreviewUrl(URL.createObjectURL(f)); }
      }}
      onPickFile={(file) => {
        formik.setFieldValue("image", file);
        setPreviewUrl(file ? URL.createObjectURL(file) : null);
      }}
    />
  );
};

export default RealtorAddEditCategory;
